import React, { useEffect, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  Archive,
  ArrowLeft as ArrowLeftIcon,
  ArweaveLogo,
  BrandJustLogo,
  Copy04,
  EtherscanLogo,
} from "@hl/base-components/lib/assets/icons.generated";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { UploadManagerProvider } from "@hl/base-components/lib/upload/context";
import { NetworkType } from "@hl/shared-features/lib/apollo/graphql.generated";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import { chainIdToNetworkType } from "@hl/shared-features/lib/features/chain";
import { useTransactionState } from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import { getBlockExplorerName } from "@hl/shared-features/lib/utils/blockExplorer";
import { AppShell, Button, Container, Grid, Space, Stack } from "@mantine/core";
import { isEmpty } from "lodash";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  _CollectionType,
  PriceType,
  TokenDistributionType,
  V1Royalty,
} from "apollo/graphql.generated";
import { mapCollectionToBlockchainEntity } from "~/components/BlockchainEntityTitle/types";
import { MINT_URL, TOOLS_URL } from "~/config";
import { CreateCollectionContextProvider } from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import CollectionDeployModal from "~/features/collections/modals/CollectionDeployModal";
import CollectionArchiveModal from "~/features/collections/sections/ContractCollectionDashboard/menu/CollectionArchiveModal";
import CollectionDuplicateModal from "~/features/collections/sections/ContractCollectionDashboard/menu/CollectionDuplicateModal";
import { ManageAuction } from "~/features/tokens/sections/ManageToken/Auction";
import CollectionLinksSection from "~/features/tokens/sections/ManageToken/CollectionLinksSection";
import { ManageMintVector } from "~/features/tokens/sections/ManageToken/MintVector";
import CreateDistributionButton from "~/features/tokens/sections/ManageToken/MintVector/CreateDistributionButton";
import { MintVectorsStatus } from "~/features/tokens/sections/ManageToken/MintVector/MintVectorsStatus";
import { MultipleMintVector } from "~/features/tokens/sections/ManageToken/MintVector/MultipleMintVector";
import { ContractProperty } from "~/features/tokens/sections/ManageToken/properties/ContractProperty";
import { FeaturedToken } from "~/features/tokens/sections/ManageToken/properties/FeaturedToken";
import { MetadataProperty } from "~/features/tokens/sections/ManageToken/properties/MetadataProperty";
import { MoreOptionsProperty } from "~/features/tokens/sections/ManageToken/properties/MoreOptionsProperty";
import { NameProperty } from "~/features/tokens/sections/ManageToken/properties/NameProperty";
import { PayoutAndRoyaltyProperty } from "~/features/tokens/sections/ManageToken/properties/PayoutAndRoyaltyProperty";

import { AppFooter } from "../../../layout/Footer";
import { AppHeader } from "../../../layout/Header";

import { DeployStatusBox } from "./DeployStatusBox";
import ManageTokenHeader from "./ManageTokenHeader";
import ManageTokenStats from "./ManageTokenStats";
import { CurateOutputProperty } from "./properties/CurateOutputProperty";
import { DescriptionProperty } from "./properties/DescriptionProperty";
import { SeriesPreviewImages } from "./properties/PreviewImages";
import {
  GetManageTokenCollectionQuery,
  useGetManageTokenAuctionsQuery,
  useGetManageTokenCollectionQuery,
} from "./queries.graphql.generated";

export const ManageCollection = () => {
  const navigate = useNavigate();
  const { trackEvent, EventType } = useAnalytics();
  const { collectionId } = useParams();
  const [searchParams] = useSearchParams();
  const txnId = searchParams.get("txnId");
  const transactionState = useTransactionState(txnId ?? "");
  const [createModalOpen, setCreateModalOpen] = useState(true);
  const [disableLoader, setDisableLoader] = useState(false);
  const [isDuplicateCollectionModalOpen, setIsDuplicateCollectionModalOpen] =
    useState(false);
  const [isArchiveCollectionModalOpen, setIsArchiveCollectionModalOpen] =
    useState(false);

  const {
    data: collectionData,
    loading: collectionLoading,
    refetch: refetchCollection,
  } = useGetManageTokenCollectionQuery({
    variables: {
      collectionId: collectionId ?? "",
      first: 10,
      firstHashes: 99999,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const collection = collectionData?.getCollection;
  const enableManageMultipleMintVector =
    collection?.flagVariations.multipleMintVectorsEnableManagement || false;

  const {
    data: auctionData,
    loading: auctionLoading,
    refetch: refetchAuction,
  } = useGetManageTokenAuctionsQuery({
    variables: {
      collectionId: collectionId ?? "",
    },
    fetchPolicy: "network-only",
    skip: !collectionData,
    notifyOnNetworkStatusChange: true,
  });
  useEffect(() => {
    trackEvent(EventType.EditToken_Viewed, { collectionId });
  }, []);

  useEffect(() => {
    if (transactionState && transactionState.updatedEvmTx) {
      refetchCollection();
    }
  }, [transactionState]);

  const refetchCollectionWithoutLoader = () => {
    setDisableLoader(true);
    refetchCollection();
  };

  useEffect(() => {
    if (!collectionLoading) {
      setDisableLoader(false);
    }
  }, [collectionLoading]);

  const deployModal = txnId ? (
    <CollectionDeployModal
      txnId={txnId}
      open={createModalOpen}
      manageCollectionHandler={() => {
        setCreateModalOpen(false);
        refetchCollection();
      }}
      onClose={() => {
        setCreateModalOpen(false);
        refetchCollection();
      }}
      onRetryUpload={function (): void {
        console.log("Can't retry in deploy mode");
      }}
    />
  ) : (
    <></>
  );

  const mintVector = collection?.mintVectors[0];
  const publicMintVector = collection?.mintVectors.find((mintVector) => {
    return mintVector.isPublic;
  });
  const auction = auctionData?.getAuctionsByCollection[0];

  if ((collectionLoading && !disableLoader) || auctionLoading) {
    return <LoadingPage />;
  }

  if (!collection) {
    return <NotFound redirectUrl={TOOLS_URL.home} />;
  }

  const isDeploying = collection.status === "IN_PROGRESS";
  const tokenUrl = MINT_URL.tokens.mint.replace(
    ":collectionId",
    collectionId ?? ""
  );

  const collectionProperties = getCollectionProperties(collection);

  const blockchainEntity = mapCollectionToBlockchainEntity(collection);

  const firstPaymentRecipient = mintVector?.paymentRecipient;

  const links = [
    {
      icon: <BrandJustLogo height={12} width={18} />,
      title: "View collection on Highlight marketplace",
      href: MINT_URL.tokens.marketplace.replace(":collectionId", collectionId!),
    },
  ];

  if (blockchainEntity.blockExplorerUrl) {
    links.push({
      //TODO: Use correct block explorer icon
      icon: <EtherscanLogo />,
      title: `View contract on ${getBlockExplorerName(
        blockchainEntity.networkData?.type || NetworkType.ETHEREUM
      )}`,
      href: blockchainEntity.blockExplorerUrl,
    });
  }

  if (blockchainEntity.arweaveUrl) {
    links.push({
      icon: <ArweaveLogo />,
      title: "View metadata on Arweave",
      href: blockchainEntity.arweaveUrl,
    });
  }

  return (
    <>
      <AppSEO title={blockchainEntity.details.name} />
      <AppShell
        header={
          <AppHeader
            showBrand={false}
            leftButtons={
              <Link to="/">
                <Button
                  color={SECONDARY_COLOR}
                  leftIcon={<ArrowLeftIcon height={12} />}
                >
                  Back
                </Button>
              </Link>
            }
          />
        }
        footer={<AppFooter />}
      >
        <CreateCollectionContextProvider>
          {deployModal}
          <UploadManagerProvider>
            <Container size="md">
              <ManageTokenHeader blockchainEntity={blockchainEntity} />
              <Space h={20} />
              {enableManageMultipleMintVector && mintVector && (
                <>
                  <ManageTokenStats collection={collection} />
                  <Space h={20} />
                </>
              )}
              <DeployStatusBox
                isDeploying={isDeploying}
                mintUrl={tokenUrl}
                collectionId={collectionId}
                chainId={collection.chainId}
                isAuction={!!auction}
              />
              <Space h={20} />
              <Grid grow gutter={32} gutterMd={80}>
                <Grid.Col span={12} md={6} order={2}>
                  <Stack spacing={40}>
                    <ContractProperty contract={collection.contract} />
                    <MetadataProperty
                      seriesTokens={collection.seriesTokens}
                      collectionMediaUrl={
                        !isEmpty(collection) && !isEmpty(collection.editions)
                          ? collection.editions![0].image
                          : ""
                      }
                      collectionAnimationUrl={
                        !isEmpty(collection) && !isEmpty(collection.editions)
                          ? collection.editions![0].animation
                          : ""
                      }
                      attributes={collectionProperties.attributes}
                      name={collection.name}
                      description={collection.description || ""}
                      isAuction={!!auction}
                      showSetBaseUriBtn={
                        collection.collectionType === _CollectionType.Series &&
                        collection.reveal === true
                      }
                      mirroredBaseUri={
                        collection.generativeDetails?.generativeCodeUri ??
                        collection.seriesDetails?.mirroredBaseUri ??
                        collection.baseUri ??
                        ""
                      }
                      collectionId={collection.id}
                      contractAddress={
                        collection.contract?.address ?? collection.address ?? ""
                      }
                      collectionType={collection.collectionType}
                      collectionSize={
                        (collection.collectionType === _CollectionType.Series ||
                        collection.collectionType ===
                          _CollectionType.GenerativeSeries
                          ? collection.size
                          : collection.editions
                          ? collection.editions[0].size
                          : mintVector?.maxPerVector ?? 0) ??
                        mintVector?.maxPerVector ??
                        0
                      }
                      chainId={collection.chainId}
                      metadataIsDecentralized={
                        !!collection.onChainBaseUri
                          ? collection.onChainBaseUri.includes("arweave")
                          : false
                      }
                      reveal={collection.reveal ?? false}
                      takenIds={
                        collection.reveal
                          ? collection.revealClaimedIds ?? undefined
                          : collection.seriesTokens?.allMintedTokenIds ??
                            undefined
                      }
                    />
                    <NameProperty
                      name={collectionProperties.name}
                      collection={collection}
                      refetch={refetchCollection}
                    />
                    {collectionProperties.description && (
                      <DescriptionProperty
                        description={collectionProperties.description}
                        collection={collection}
                        refetch={refetchCollection}
                      />
                    )}
                    {collection.collectionType === _CollectionType.Series && (
                      <SeriesPreviewImages collection={collection} />
                    )}
                    {collection && (
                      <FeaturedToken
                        collection={collection}
                        refetch={refetchCollectionWithoutLoader}
                      />
                    )}
                    {(collection.generativeDetails?.allowedHashes?.length ??
                      0) !== 0 && (
                      <CurateOutputProperty
                        collectionId={collection.id}
                        generativeDetails={collection.generativeDetails}
                        genSeriesHashes={collection.genSeriesHashes}
                      />
                    )}
                    <PayoutAndRoyaltyProperty
                      distributionType={
                        auction
                          ? TokenDistributionType.Auction
                          : TokenDistributionType.Mint
                      }
                      publicMintVector={publicMintVector}
                      paymentRecipient={firstPaymentRecipient}
                      royalty={collectionProperties.royalty}
                      collectionId={collection.id}
                      editionId={collection.editionId}
                      collectionType={collection.collectionType}
                      chainId={collection.chainId}
                      address={collection.address}
                      isCollectionReveal={collection.reveal}
                      refetchCollection={refetchCollection}
                      refetchAuction={refetchAuction}
                      enableEdit={!isDeploying && !auction}
                    />
                    <MoreOptionsProperty
                      nonTransferable={blockchainEntity.details.nonTransferable}
                      usingOpenseaBlocklist={collection.usingOpenseaBlocklist}
                      editionId={collection.editionId ?? undefined}
                      isDirectMint={mintVector?.isDirectMint ?? false}
                      collectionId={collection.id}
                      creditCardEnabled={collection.creditCardEnabled ?? false}
                      collectionType={collection.collectionType}
                      isOnchainDutchAuction={
                        publicMintVector?.priceType === PriceType.DutchAuction
                      }
                      hasPublicSale={!!publicMintVector}
                      network={chainIdToNetworkType(collection.chainId)}
                      refetch={refetchCollection}
                      hidden={collection.hidden}
                    />
                    <Stack spacing={8}>
                      <Button
                        color={OUTLINE_COLOR}
                        leftIcon={<Copy04 />}
                        onClick={() => setIsDuplicateCollectionModalOpen(true)}
                      >
                        Duplicate collection
                      </Button>
                      <Button
                        color={OUTLINE_COLOR}
                        leftIcon={<Archive />}
                        onClick={() => setIsArchiveCollectionModalOpen(true)}
                      >
                        Archive collection
                      </Button>
                    </Stack>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={12} md={4} order={1} orderMd={3}>
                  <Stack
                    maw={{
                      base: "100%",
                      md: 356,
                    }}
                    ml="auto"
                  >
                    {!mintVector && !auction && (
                      <CreateDistributionButton
                        mt={34}
                        collection={collection}
                      />
                    )}
                    {mintVector && !enableManageMultipleMintVector && (
                      <ManageMintVector
                        mt={32}
                        collection={collection}
                        mintVector={mintVector}
                      />
                    )}
                    {mintVector && enableManageMultipleMintVector && (
                      <>
                        <MintVectorsStatus
                          mintVectors={
                            collection?.mintVectors.filter((mintVector) => {
                              return !mintVector.sponsored; // don't show sponsored mint vectors
                            }) || []
                          }
                          collection={collection}
                        />
                        <MultipleMintVector
                          mt={32}
                          collection={collection}
                          mintVectors={
                            collection?.mintVectors.filter((mintVector) => {
                              return !mintVector.sponsored; // don't show sponsored mint vectors
                            }) || []
                          }
                        />
                      </>
                    )}
                    {auction && (
                      <ManageAuction
                        mt={32}
                        collection={collection}
                        auction={auction}
                        refetchCollection={refetchCollection}
                        refetchAuction={refetchAuction}
                      />
                    )}
                    <CollectionLinksSection mt={40} links={links} />
                  </Stack>
                </Grid.Col>
              </Grid>
            </Container>
          </UploadManagerProvider>
        </CreateCollectionContextProvider>
      </AppShell>

      {isDuplicateCollectionModalOpen && (
        <CollectionDuplicateModal
          isOpened
          entity={blockchainEntity}
          onClose={() => setIsDuplicateCollectionModalOpen(false)}
          onFinished={() => navigate(TOOLS_URL.home)}
        />
      )}
      {isArchiveCollectionModalOpen && (
        <CollectionArchiveModal
          isOpened
          entity={blockchainEntity}
          onClose={() => setIsArchiveCollectionModalOpen(false)}
        />
      )}
    </>
  );
};

export const getCollectionProperties = (
  collection: GetManageTokenCollectionQuery["getCollection"]
) => {
  if (collection.editions && collection.editions.length) {
    const edition = collection.editions[0];

    return {
      name: edition.name,
      description: edition.description,
      image: edition.image,
      royalty: edition.royalty,
      onChainMetadata: edition.onChainMetadata,
      attributes: edition.attributes,
    };
  }
  if (collection.tokens && collection.tokens.length) {
    const token = collection.tokens[0];
    return {
      name: token.name,
      description: token.description,
      image: token.image,
      royalty: collection.royalty,
    };
  }
  return {
    name: collection?.name || "",
    description: collection.description || "",
    image: "",
    royalty:
      collection.royalty ||
      ({
        royaltyRecipient: undefined,
        royaltyPercentageString: undefined,
      } as V1Royalty),
  };
};
